// Recordatorios Inventario Semanal (Bodega Bloque 5) — /configuracion/recordatorios-inventario
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import {
  BellRing, Mail, Plus, X, Send, Save, Clock, Users,
  AlertTriangle, History, ClipboardCheck, PauseCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const DIAS_SEMANA = [
  { value: 0, label: 'Lun' }, { value: 1, label: 'Mar' }, { value: 2, label: 'Mié' },
  { value: 3, label: 'Jue' }, { value: 4, label: 'Vie' }, { value: 5, label: 'Sáb' },
  { value: 6, label: 'Dom' },
];

function EmailListInput({ label, items, onChange, placeholder, testId = 'emails' }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v || items.includes(v)) { setDraft(''); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error('Email inválido'); return; }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (e) => onChange(items.filter((x) => x !== e));
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> {label}
      </label>
      <div className="flex gap-1 mb-1.5">
        <Input
          type="email" value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder} className="text-sm"
          data-testid={`${testId}-input`}
        />
        <Button size="sm" type="button" onClick={add} variant="outline" data-testid={`${testId}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">Sin destinatarios.</span>}
        {items.map((e) => (
          <Badge key={e} variant="secondary" className="gap-1 pr-1" data-testid={`${testId}-chip`}>
            {e}
            <button type="button" onClick={() => remove(e)} className="hover:bg-red-200 rounded-full p-0.5">
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function RecordatoriosInventarioPage() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, h] = await Promise.all([
        api.get('/recordatorios/inventario-semanal/config'),
        api.get('/recordatorios/inventario-semanal/preview'),
        api.get('/recordatorios/inventario-semanal/log?limit=10'),
      ]);
      setCfg(c.data);
      setPreview(p.data);
      setHistory(h.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando configuración');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleDia = (d) => {
    const curr = cfg.dias_semana || [];
    setCfg({
      ...cfg,
      dias_semana: curr.includes(d) ? curr.filter((x) => x !== d) : [...curr, d].sort(),
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/recordatorios/inventario-semanal/config', cfg);
      setCfg(res.data);
      toast.success('Configuración guardada');
      const r = await api.get('/recordatorios/inventario-semanal/preview');
      setPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error guardando');
    } finally { setSaving(false); }
  };

  const handleSendNow = async () => {
    if ((!cfg.to || cfg.to.length === 0) && !cfg.notificar_responsable) {
      toast.error('Agrega destinatarios o activa "Notificar responsable"');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/recordatorios/inventario-semanal/send-now');
      const d = res.data;
      if (d.sent) {
        toast.success(
          `Email enviado · ${d.atrasados} atrasados · ${d.abandonados} abandonados`,
        );
      } else {
        toast.info(`No enviado: ${d.reason || d.error || 'sin items'}`);
      }
      const r = await api.get('/recordatorios/inventario-semanal/log?limit=10');
      setHistory(r.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error enviando');
    } finally { setSending(false); }
  };

  if (loading || !cfg) {
    return <div className="p-6 space-y-4"><Skeleton className="h-32 w-full" /><Skeleton className="h-48 w-full" /></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="recordatorios-inventario-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardCheck className="w-6 h-6 text-indigo-600" /> Recordatorios · Inventario Semanal
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Alerta automática por email cuando los pañoles no hacen su ciclo de inventario a tiempo.
        </p>
      </div>

      <Card data-testid="card-recordatorio-inv">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BellRing className="w-5 h-5" /> Avisos de atrasos en ciclo semanal
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Escanea todos los pañoles activos y notifica los que superan el umbral + ciclos abiertos abandonados.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{cfg.enabled ? 'Activo' : 'Pausado'}</span>
              <Switch
                checked={cfg.enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                data-testid="switch-enabled"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Configuración */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> Días sin ciclo → atraso
              </label>
              <Input
                type="number" min={1} max={60}
                value={cfg.dias_atraso_min}
                onChange={(e) => setCfg({ ...cfg, dias_atraso_min: parseInt(e.target.value, 10) || 7 })}
                data-testid="input-dias-atraso"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">Default: 7.</p>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
                <PauseCircle className="w-3.5 h-3.5 text-amber-500" /> Días sin scan → abandonado
              </label>
              <Input
                type="number" min={1} max={30}
                value={cfg.dias_abandono_min}
                onChange={(e) => setCfg({ ...cfg, dias_abandono_min: parseInt(e.target.value, 10) || 3 })}
                data-testid="input-dias-abandono"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">Ciclos abiertos inactivos. Default: 3.</p>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Hora de envío
              </label>
              <Input
                type="time"
                value={cfg.hora || '08:00'}
                onChange={(e) => setCfg({ ...cfg, hora: e.target.value })}
                data-testid="input-hora"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5">Días de envío</label>
            <div className="flex flex-wrap gap-1.5" data-testid="dias-semana-group">
              {DIAS_SEMANA.map((d) => {
                const active = (cfg.dias_semana || []).includes(d.value);
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleDia(d.value)}
                    className={`px-3 py-1 text-xs rounded-md border transition ${
                      active
                        ? 'bg-indigo-600 text-white border-indigo-700'
                        : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`btn-dia-${d.value}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Default: Lunes · Miércoles · Viernes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <EmailListInput
              label="Destinatarios (TO)"
              items={cfg.to || []}
              onChange={(v) => setCfg({ ...cfg, to: v })}
              placeholder="rmartinez@chirinosteel.com"
              testId="to"
            />
            <EmailListInput
              label="Con copia (CC)"
              items={cfg.cc || []}
              onChange={(v) => setCfg({ ...cfg, cc: v })}
              placeholder="c.chirino@chirinosteel.com"
              testId="cc"
            />
          </div>

          <div className="flex items-center gap-2 bg-slate-50 border rounded-md p-3">
            <Checkbox
              id="chk-notif-resp"
              checked={cfg.notificar_responsable}
              onCheckedChange={(v) => setCfg({ ...cfg, notificar_responsable: !!v })}
              data-testid="chk-notif-responsable"
            />
            <label htmlFor="chk-notif-resp" className="text-sm cursor-pointer">
              <b>Notificar al responsable del pañol</b> (si tiene email en usuarios) — se agrega como CC automáticamente.
            </label>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
            </Button>
            <Button
              variant="outline" onClick={handleSendNow} disabled={sending}
              data-testid="btn-send-now"
            >
              <Send className="w-4 h-4 mr-1" /> {sending ? 'Enviando...' : 'Enviar ahora (test)'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card data-testid="card-preview">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4" /> Preview · qué se enviaría ahora mismo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-center">
              <div className="text-3xl font-bold text-rose-700" data-testid="count-atrasados">
                {preview?.count_atrasados || 0}
              </div>
              <div className="text-xs text-rose-600">pañoles atrasados (&gt;{preview?.dias_atraso_min || 7}d)</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-center">
              <div className="text-3xl font-bold text-amber-700" data-testid="count-abandonados">
                {preview?.count_abandonados || 0}
              </div>
              <div className="text-xs text-amber-600">ciclos abandonados (≥{preview?.dias_abandono_min || 3}d sin scan)</div>
            </div>
          </div>

          {(preview?.atrasados || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">🔴 Atrasados</div>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left px-2 py-1">Pañol</th>
                    <th className="text-left px-2 py-1">Responsable</th>
                    <th className="px-2 py-1">Htas</th>
                    <th className="text-left px-2 py-1">Atraso</th>
                  </tr></thead>
                  <tbody>
                    {(preview.atrasados || []).slice(0, 10).map((p) => (
                      <tr key={p.panol_id} className="border-t">
                        <td className="px-2 py-1 font-semibold">{p.panol_nombre}</td>
                        <td className="px-2 py-1 text-slate-600">{p.responsable_nombre || '—'}</td>
                        <td className="px-2 py-1 text-center">{p.total_herramientas}</td>
                        <td className="px-2 py-1 text-rose-700 font-semibold">
                          {p.nunca ? 'Nunca' : `${p.dias_desde_cierre}d`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(preview?.abandonados || []).length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">🟡 Abandonados</div>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left px-2 py-1">Pañol</th>
                    <th className="text-left px-2 py-1">Iniciado por</th>
                    <th className="px-2 py-1">Progreso</th>
                    <th className="text-left px-2 py-1">Inactivo</th>
                  </tr></thead>
                  <tbody>
                    {(preview.abandonados || []).slice(0, 10).map((c) => (
                      <tr key={c.ciclo_id} className="border-t">
                        <td className="px-2 py-1 font-semibold">{c.panol_nombre}</td>
                        <td className="px-2 py-1 text-slate-600">{c.iniciado_por}</td>
                        <td className="px-2 py-1 text-center">{c.scans_count}/{c.esperados_count}</td>
                        <td className="px-2 py-1 text-amber-700 font-semibold">{c.dias_inactivo}d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(!preview?.count_atrasados && !preview?.count_abandonados) && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-800">
              ✅ Sin atrasos ni ciclos abandonados. Todos los pañoles al día.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historial */}
      <Card data-testid="card-historial">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4" /> Historial de envíos (últimos 10)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">Sin envíos aún.</div>
          ) : (
            <div className="space-y-1">
              {history.map((h, i) => (
                <div key={i} className="text-xs flex items-center gap-2 border-b pb-1">
                  <Badge variant={h.success ? 'default' : 'destructive'} className="text-[10px]">
                    {h.success ? 'OK' : 'ERROR'}
                  </Badge>
                  <span className="text-slate-500">{h.timestamp?.slice(0, 16).replace('T', ' ')}</span>
                  <span>· {h.atrasados_count} atrasados · {h.abandonados_count} abandonados</span>
                  {h.manual && <Badge variant="outline" className="text-[10px]">Manual</Badge>}
                  {h.error && <span className="text-rose-600 truncate">{h.error}</span>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
